import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Card } from '@stripe/stripe-js';
import { distinctUntilChanged, filter, lastValueFrom, map, Observable, of, switchMap, take } from 'rxjs';
import { TENANT_CONTEXT } from '../network-interceptor';
import { AppConfigService } from '../providers/config.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { StateService } from './state.service';

@Injectable({
  providedIn: 'root',
})
export class CardService {
  private config = inject(AppConfigService);
  private http = inject(HttpClient);
  private state = inject(StateService);
  private loader = inject(ObservableDataLoaderService);

  public getCardData(): Observable<{ paymentMethod: string | null; card: Card | null }> {
    if (!['admin', 'owner'].includes(this.state.activeTenantRole)) return of({ paymentMethod: null, card: null });
    return this.state.activeTenant$.pipe(
      filter(Boolean),
      distinctUntilChanged((a, b) => a?.id === b?.id),
      switchMap(tenant => {
        return this.loader.get(`card_${tenant.id}`, () => {
          return this.http
            .get<{ data: { id: string; card: Card; created: number }[] }>(`${this.config.apiEndpoint}/billing/cards`, {
              context: new HttpContext().set(TENANT_CONTEXT, tenant.id),
            })
            .pipe(
              map(resp => {
                if (!resp.data.length) return { paymentMethod: null, card: null };
                return {
                  paymentMethod: resp.data[0].id,
                  card: resp.data[0].card,
                };
              })
            );
        });
      })
    );
  }

  // Don't call this without first making sure the customer doesn't have an active subscription
  public async deletePaymentMethod() {
    await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/billing/cards`, {}));
    this.loader.updateFuzzy('card_', () => ({ paymentMethod: null, card: null }));
  }

  public forceCardRefresh() {
    return this.state.activeTenant$.pipe(
      take(1),
      switchMap(tenant => {
        return this.loader.forceDataRefresh<{ paymentMethod: string | null; card: Card | null }>(`card_${tenant.id}`);
      })
    );
  }
}
